import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { LinkedinSignalResharedPost } from "@/pages/Private/pages/ProfileTracker/schema/trackerSignalResult";

import { LinkedinSignalPostMedia } from "./LinkedinSignalPostMedia";

interface LinkedinResharedPostProps {
	reshared: LinkedinSignalResharedPost;
	/** Link-out target for reshared media tiles (the resharing post). */
	postUrl?: string | null;
}

/**
 * Nested quoted card for a repost: the original author, their text and any of
 * their own media, rendered inside a bordered frame beneath the resharer's
 * commentary — the way LinkedIn stacks a reshared post.
 */
export const LinkedinResharedPost: FunctionComponent<LinkedinResharedPostProps> = ({
	reshared,
	postUrl,
}) => {
	const { t } = useTranslation();
	const [hasImageError, setHasImageError] = useState(false);
	const authorName = reshared.authorName || t("profileTracker.signalTimeline.linkedinPost");
	const media = (reshared.media ?? []).filter((item) => Boolean(item?.url));

	const avatar =
		reshared.authorImage && !hasImageError ? (
			<img
				alt={authorName}
				className="h-7 w-7 rounded-full object-cover"
				src={reshared.authorImage}
				onError={() => setHasImageError(true)}
			/>
		) : (
			<div className="flex h-7 w-7 items-center justify-center rounded-full bg-background-light text-xs font-semibold text-text-meta">
				{authorName.charAt(0).toUpperCase()}
			</div>
		);

	return (
		<div
			className="mt-3 rounded-lg border border-border-light p-3"
			data-testid="linkedin-reshared-post"
		>
			<div className="mb-2 flex items-center gap-2">
				{avatar}
				<div className="min-w-0">
					<div className="truncate text-[13px] font-semibold text-text">{authorName}</div>
					{reshared.authorHeadline && (
						<div className="truncate text-xs text-text-meta">{reshared.authorHeadline}</div>
					)}
				</div>
			</div>

			{reshared.text && (
				<p className="whitespace-pre-wrap text-[13px] leading-5 text-text">{reshared.text}</p>
			)}

			{media.length > 0 && <LinkedinSignalPostMedia media={media} postUrl={postUrl} />}
		</div>
	);
};
